"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";
import { HubSpotForm } from "@/components/ui/hubspot-form";
import { HUBSPOT_FORMS, HUBSPOT_PORTAL_ID, HUBSPOT_REGION } from "@/data/hubspot-forms";

type Item = { title: string; description: string };
type SolutionItem = { title: string; description: string; imageAlt: string };

export default function AutoconsumoFotovoltaicoPage() {
  const t = useTranslations("autoconsumoPVPage");
  const highlights = t.raw("highlights.items") as Item[];
  const benefits = t.raw("benefits.items") as Item[];
  const mainParagraphs = t.raw("main.paragraphs") as string[];
  const domestic = t.raw("solutions.domestic") as SolutionItem;
  const industrial = t.raw("solutions.industrial") as SolutionItem;

  return (
    <div className="min-h-screen bg-surface-light text-ink dark:bg-surface-dark dark:text-white">
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-brand-navy text-white dark:bg-surface-dark-deep">
        <div className="absolute inset-0">
          <Image
            src="/static/services/autoconsumo-domestico/sotogrande-cadiz.webp"
            alt={t("hero.imageAlt")}
            fill
            className="object-cover opacity-80"
            priority
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/40 via-brand-navy/60 to-brand-navy dark:from-black/30 dark:via-surface-dark-deep/70 dark:to-surface-dark-deep" />
        </div>
        <div className="relative mx-auto flex max-w-6xl flex-col gap-6 px-4 pb-16 pt-28 text-center md:pb-24 md:pt-32">
          <span className="mx-auto rounded-full bg-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-label text-[#f7f2ec] shadow-lg">
            {t("hero.badge")}
          </span>
          <h1 className="type-h1">{t("hero.title")}</h1>
          <p className="mx-auto max-w-3xl text-lg md:text-xl text-mist">{t("hero.subtitle")}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild className="px-8 py-3 text-lg uppercase">
              <Link href="#contact-form">{t("hero.cta1")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Highlights — icon block */}
      <section className="px-4 section-y">
        <div className="mx-auto max-w-6xl">
          <p className="text-center text-xs font-semibold uppercase tracking-label text-brand-red">
            {t("highlights.eyebrow")}
          </p>
          <h2 className="mt-2 text-center text-brand-navy dark:text-white type-h2">
            {t("highlights.title")}
          </h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {highlights.map((item) => (
              <div
                key={item.title}
                className="surface-card p-6"
              >
                <h3 className="text-lg font-bold text-brand-navy dark:text-white">{item.title}</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Por qué — text + photo */}
      <section className="bg-white px-4 section-y dark:bg-surface-dark">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <div className="space-y-5">
            <p className="text-xs font-semibold uppercase tracking-label text-brand-red">{t("main.eyebrow")}</p>
            <h2 className="text-brand-navy dark:text-white type-h2">{t("main.title")}</h2>
            {mainParagraphs.map((p, i) => (
              <p key={i} className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                {p}
              </p>
            ))}
          </div>
          <div className="media-frame">
            <Image
              src="/static/services/autoconsumo-domestico/jerez-de-la-frontera-cadiz-01.webp"
              alt={t("main.imageAlt")}
              width={840}
              height={1050}
              className="h-full w-full object-cover"
              sizes="(min-width: 1024px) 42vw, 100vw"
            />
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="bg-gradient-to-br from-brand-navy to-brand-navy-light px-4 section-y text-white">
        <div className="mx-auto max-w-6xl">
          <h3 className="text-center text-2xl sm:text-3xl font-black">{t("benefits.title")}</h3>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((item) => (
              <div key={item.title} className="rounded-2xl bg-white/12 p-5 shadow-xl backdrop-blur">
                <h4 className="text-base sm:text-lg font-semibold">{item.title}</h4>
                <p className="mt-2 text-sm text-white/85">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Soluciones — doméstico + industrial */}
      <section className="px-4 section-y">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs font-semibold uppercase tracking-label text-brand-red">
            {t("solutions.eyebrow")}
          </p>
          <h2 className="mt-2 text-brand-navy dark:text-white type-h2">
            {t("solutions.title")}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{t("solutions.subtitle")}</p>

          <div className="mt-10 space-y-12">
            {/* Doméstico */}
            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <div className="media-frame">
                <Image
                  src="/static/services/autoconsumo-domestico/alcala-del-rio-sevilla.webp"
                  alt={domestic.imageAlt}
                  width={840}
                  height={560}
                  className="h-full w-full object-cover"
                  sizes="(min-width: 1024px) 45vw, 100vw"
                />
              </div>
              <div className="space-y-4">
                <h3 className="text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">{domestic.title}</h3>
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                  {domestic.description}
                </p>
              </div>
            </div>

            {/* Industrial */}
            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div className="space-y-4">
                <h3 className="text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">{industrial.title}</h3>
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                  {industrial.description}
                </p>
              </div>
              <div className="media-frame">
                <Image
                  src="/static/services/autoconsumo-industrial/san-juan-de-aznalfarache-sevilla.webp"
                  alt={industrial.imageAlt}
                  width={840}
                  height={560}
                  className="h-full w-full object-cover"
                  sizes="(min-width: 1024px) 45vw, 100vw"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HubSpot Form */}
      <section className="bg-brand-navy px-4 section-y text-white dark:bg-surface-dark-deep">
        <div className="hubspot-light mx-auto max-w-5xl rounded-card bg-white p-6 sm:p-8 text-ink shadow-card-dark">
          <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-label text-brand-red">{t("form.eyebrow")}</p>
          <h3 className="mt-3 text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">{t("form.title")}</h3>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{t("form.subtitle")}</p>
          </div>
          <div className="mt-6">
          <HubSpotForm portalId={HUBSPOT_PORTAL_ID} formId={HUBSPOT_FORMS.contact} region={HUBSPOT_REGION} />
          </div>
        </div>
      </section>
    </div>
  );
}
